import { Controller } from '@hotwired/stimulus'
import { isFileDrag } from '../upload_helpers'
import { getCurrentBreakpoint } from '../editor/helpers.js'

export default class extends Controller {
  static values = {
    width: Number,
    widthUnit: String,
    applyToContainer: Boolean,
  }

  static targets = ['leftHandle', 'rightHandle', 'resizeWrapper']

  connect() {
    this.dragDepth = 0
    this.teardown = []

    if (!this.hasLeftHandleTarget) return

    this.addDragEvents(this.leftHandleTarget, 'left')
    this.addDragEvents(this.rightHandleTarget, 'right')

    this.imageTag = this.element.parentNode.querySelector('img')
    this.container = this.element.closest('.builder-image')
    this.additionalOffset = 0
    this.justify = null

    // Deliberately no getComputedStyle here. connect() runs for every image in a section
    // that has just been re-rendered, and the width write below invalidates layout -- so a
    // read here forced a full style recalc of the freshly inserted subtree, ONCE PER IMAGE
    // (~220 ms each on a large page; eight images cost 1.75 s of a single edit). Controllers
    // that only read pay that recalc once between them.
    //
    // The two values those reads produced are only needed once a drag starts, so they moved
    // to dragGeometry(), and the resizer now takes its justify-content from CSS
    // (`.image-resizer { justify-content: inherit }`) instead of copying the picture's.
    this.elementToResize = this.applyToContainerValue
      ? this.container
      : this.imageTag
    this.resizeWrapperTarget.style.width = this.applyToContainerValue
      ? `100%`
      : `${this.widthValue}${this.widthUnitValue}`
  }

  disconnect() {
    this.teardown.forEach((remove) => remove())
    this.teardown = []
  }

  // Resolved when a drag actually starts -- once per drag the user performs, against a
  // settled layout -- rather than once per image on every re-render.
  dragGeometry() {
    if (this.applyToContainerValue) {
      // The container can have custom padding which we need to take into account when
      // calculating the delta.
      const style = getComputedStyle(this.container)
      this.additionalOffset =
        parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
      this.justify = null
    } else {
      this.additionalOffset = 0
      this.justify = getComputedStyle(this.imageTag.parentNode).justifyContent
    }
  }

  addDragEvents(handle, side) {
    let startX, startWidth

    const onMouseDown = (e) => {
      e.preventDefault()
      e.stopImmediatePropagation()
      this.dragGeometry()
      startX = e.clientX
      startWidth = this.resizeWrapperTarget.offsetWidth
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    }

    const onMouseMove = (e) => {
      e.stopImmediatePropagation()
      e.preventDefault()

      const delta =
        (e.clientX - startX + this.additionalOffset) *
        (this.justify === 'center' ? 2 : 1)
      let newWidth

      if (side === 'right') {
        newWidth = startWidth + delta
      } else {
        newWidth = startWidth - delta
      }

      if (this.widthUnitValue === '%') {
        const parentWidth = this.elementToResize.parentNode.offsetWidth
        let perc = Math.floor((newWidth / parentWidth) * 100)
        if (perc < 1) {
          perc = 1
        }

        if (perc > 100) {
          perc = 100
        }

        this.elementToResize.style.width = `${perc}%`
        if (!this.applyToContainerValue)
          this.resizeWrapperTarget.style.width = `${perc}%`
        this.widthValue = perc
      } else {
        if (newWidth < 20) {
          newWidth = 20
        }
        this.imageTag.style.width = `${newWidth}px`
        this.resizeWrapperTarget.style.width = `${newWidth}px`

        this.widthValue = newWidth
      }

      return false
    }

    const onMouseUp = (e) => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)

      e.stopImmediatePropagation()

      const nodeId = this.element.closest('.js-builder-node').dataset.nodeId

      // Save to the breakpoint currently being previewed, not the one baked in at
      // render time — otherwise resizing while previewing mobile writes to the base
      // breakpoint and applies everywhere.
      const breakpoint = getCurrentBreakpoint()

      this.dispatch('nodeSettingChanged', {
        target: window.parent.document,
        detail: {
          nodeId,
          name: 'image',
          setting_name: 'width',
          value: this.widthValue,
          breakpoint,
          // Only skip the previewer re-render on base, where the optimistic inline
          // width we set during the drag is correct at every viewport. On tab/mob we
          // must re-render so the width moves into a breakpoint-scoped CSS rule;
          // otherwise the inline style overrides the media query and the new width
          // bleeds into every breakpoint.
          meta:
            breakpoint === 'base' ? { skip_rerender_in_previewer: '1' } : {},
        },
      })
    }

    const stop = (e) => e.stopImmediatePropagation()
    handle.addEventListener('dragstart', stop)
    handle.addEventListener('mousedown', onMouseDown)
    handle.addEventListener('click', stop)

    // The canvas replaces this subtree on every edit, so without teardown each re-render
    // leaves another set of handlers behind on the detached handles.
    this.teardown.push(() => {
      handle.removeEventListener('dragstart', stop)
      handle.removeEventListener('mousedown', onMouseDown)
      handle.removeEventListener('click', stop)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    })
  }

  openAssetPicker() {
    setTimeout(() => {
      this.dispatch('openAssetPicker', {
        target: window.parent.document,
        detail: {
          setting: 'image',
          field: 'asset_id',
        },
      })
    }, 250)
  }

  dragEnter(e) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    this.dragDepth += 1
    this.showDropOverlay()
  }

  dragOver(e) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    this.showDropOverlay()
  }

  dragLeave(e) {
    if (!isFileDrag(e)) return
    this.dragDepth = Math.max(0, this.dragDepth - 1)
    if (this.dragDepth === 0) this.hideDropOverlay()
  }

  handleDrop(e) {
    if (!isFileDrag(e)) return
    e.preventDefault()
    // The body canvas drop handler uses capture mode, so stopPropagation won't prevent it.
    // The actual guard is the `imagePlaceholderSelector` check inside handleCanvasDrop.
    e.stopPropagation()

    this.dragDepth = 0
    this.hideDropOverlay()

    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/')
    )
    if (files.length === 0) return

    const nodeId = this.element.closest('.js-builder-node')?.dataset?.nodeId
    if (!nodeId) return
    this.replaceImageWithUpload(files[0], nodeId)
  }

  showDropOverlay() {
    this.element.classList.add('image-drop-target')
  }

  hideDropOverlay() {
    this.element.classList.remove('image-drop-target')
  }

  replaceImageWithUpload(file, nodeId) {
    // Store the file in parent window for access after dialog opens
    const fileId = `pending_upload_${nodeId}_${Date.now()}`
    window.parent.__pendingUploads = window.parent.__pendingUploads || {}
    window.parent.__pendingUploads[fileId] = file

    this.dispatch('replaceImageWithUpload', {
      target: window.parent.document,
      detail: {
        nodeId,
        pendingUploadId: fileId,
      },
    })
  }
}
